import { Link } from '@router'
import type { Recipe } from '@hewey/shared'
import { useImageSrc } from '@hooks/useImageSrc.ts'
import styles from './RecipeCard.module.css'
import { RecipeImage } from './RecipeImage.tsx'
import { effortLabel } from './tagSummary.ts'
import { formatTotalTime, totalMins } from './time.ts'

type Props = {
	recipe: Recipe
	// True while the client is still within the 90-second polling window for a
	// pending tagging job. After that the badge is hidden regardless, matching
	// "best-effort, no failure UI" — see notes/recipes-phase-kickoff.md.
	taggingFresh: boolean
	// The View-all list uses a horizontal thumbnail + title row. Everything
	// else uses the image-forward feature card.
	compact?: boolean
}

// Total time comes from the scraped prep+cook minutes (present on any recipe);
// effort comes from the tags (only once tagged). Either side may be absent —
// the card just omits it.
const cardMeta = (
	recipe: Recipe,
): { time: string | null; effort: string | null } => {
	const mins = totalMins(recipe)
	const tags = recipe.taggingStatus === 'tagged' ? recipe.systemTags : null
	return {
		time: mins !== null ? formatTotalTime(mins) : null,
		effort: tags ? effortLabel(tags.effort) : null,
	}
}

export const RecipeCard = ({
	recipe,
	taggingFresh,
	compact = false,
}: Props) => {
	const { time, effort } = cardMeta(recipe)
	const imageSrc = useImageSrc('recipes', recipe.id, recipe.imageUrl)

	if (compact) {
		const meta = [time, effort].filter(Boolean).join(' · ')
		return (
			<Link
				href={`/recipes/${recipe.id}`}
				className={`${styles.card} ${styles.compact}`}
			>
				<div className={styles.thumb}>
					<RecipeImage
						src={imageSrc}
						seed={recipe.id}
						tags={recipe.systemTags}
						title={recipe.title}
						className={styles.thumbImg}
					/>
				</div>
				<div className={styles.compactBody}>
					<h3 className={styles.compactTitle}>{recipe.title}</h3>
					{meta ? <p className={styles.compactMeta}>{meta}</p> : null}
				</div>
			</Link>
		)
	}

	const showTaggingBadge = recipe.taggingStatus === 'pending' && taggingFresh

	return (
		<Link
			href={`/recipes/${recipe.id}`}
			className={`${styles.card} ${styles.feature}`}
		>
			<div className={styles.featureImage}>
				<RecipeImage
					src={imageSrc}
					seed={recipe.id}
					tags={recipe.systemTags}
					title={recipe.title}
					className={styles.image}
				/>
				{showTaggingBadge ? (
					<span className={styles.taggingBadge}>Tagging…</span>
				) : null}
			</div>
			<div className={styles.featureBody}>
				<h3 className={styles.featureTitle}>{recipe.title}</h3>
				{effort || time ? (
					<div className={styles.featureMeta}>
						<span>{effort ?? ''}</span>
						<span>{time ?? ''}</span>
					</div>
				) : null}
			</div>
		</Link>
	)
}

type PendingProps = { url: string }
export const PendingRecipeCard = ({ url }: PendingProps) => (
	<div className={`${styles.card} ${styles.feature} ${styles.pending}`}>
		<div className={styles.featureImage}>
			<div className={styles.imagePlaceholder}>…</div>
		</div>
		<div className={styles.featureBody}>
			<h3 className={styles.featureTitle}>Adding recipe…</h3>
			<p className={styles.pendingUrl}>{url}</p>
		</div>
	</div>
)
